import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  EDITOR_FONT_FAMILIES,
  editorTextBoxRoleLabel,
  orderedRoles,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type {
  EditorCharacterStyleLayer,
  EditorContentRoleValue,
  EditorSelection,
  EditorTextBoxMount,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import {
  AuthoringModalFrame,
  AuthoringPreviewCard,
  AuthoringStyleSection,
  useAuthoringCommit,
  useAuthoringStyleDraft,
} from './EditorTextBoxAuthoringModal'
import * as S from './EditorTextBoxModal.styled'

const FONT_FAMILIES = EDITOR_FONT_FAMILIES

const ROLES = orderedRoles([
  'heading',
  'deceased_name',
  'subheading',
  'body',
  'footer',
] as const)

export type EditorRoleDecorationLayers = Partial<
  Record<EditorContentRoleValue, EditorCharacterStyleLayer>
>

export type ThemeRoleDecorationModalProps = {
  getMount: () => EditorTextBoxMount | null
  getHostEl: () => HTMLDivElement | null
  decorations?: EditorRoleDecorationLayers
  initialRole?: EditorContentRoleValue
  savedSel?: EditorSelection | null
  onAuthorDecoration?: (
    role: EditorContentRoleValue,
    character: EditorCharacterStyleLayer,
  ) => Promise<boolean>
  onClose: () => void
}

const ThemeRoleDecorationModal = ({
  getMount,
  getHostEl,
  decorations = {},
  initialRole = 'heading',
  savedSel = null,
  onAuthorDecoration,
  onClose,
}: ThemeRoleDecorationModalProps) => {
  const { t } = useTranslation()
  const [role, setRole] = useState<EditorContentRoleValue>(initialRole)
  const roleLabel = editorTextBoxRoleLabel(t, role)
  const [confirm, setConfirm] = useState<{ apply: boolean } | null>(null)

  const seed = useMemo(() => decorations?.[role] ?? {}, [decorations, role])
  const { effective, authored, commitStyle, resetDraft } =
    useAuthoringStyleDraft(seed)
  const { saving, error, setError, run } = useAuthoringCommit()

  const isEditingExisting = Object.keys(seed).length > 0

  const selectRole = (next: EditorContentRoleValue) => {
    setRole(next)
    resetDraft()
    setError(null)
    setConfirm(null)
  }

  const close = () => {
    getHostEl()?.focus()
    getMount()?.placeCaretAtPos({ blockIndex: 0, offset: 0 })
    onClose()
  }

  const persist = async (apply: boolean) => {
    const ok = await run(
      () => onAuthorDecoration?.(role, { ...authored }),
      t('editor.textBox.modals.role.saveError'),
    )
    if (!ok) return
    // Applying tags the paragraph(s) the selection was on with the role; the
    // decoration just saved dresses them through the cascade, so nothing is
    // written onto the runs and a later re-authoring re-dresses them too.
    if (apply) {
      getHostEl()?.focus()
      if (savedSel) getMount()?.placeRangeAtPos(savedSel.anchor, savedSel.focus)
      getMount()?.setParagraphRole(role)
    }
    close()
  }

  const requestSave = (apply: boolean) => {
    if (isEditingExisting) setConfirm({ apply })
    else persist(apply)
  }

  const footer = confirm ? (
    <S.Footer>
      <S.Subtitle data-editor-text-role-confirm>
        {t('editor.textBox.modals.role.confirm', {
          role: roleLabel,
          roleLower: roleLabel.toLowerCase(),
        })}
      </S.Subtitle>
      <S.FooterSpacer />
      <S.GhostBtn
        type="button"
        data-editor-text-role-confirm-cancel
        onClick={() => setConfirm(null)}
      >
        {t('editor.textBox.modals.common.cancel')}
      </S.GhostBtn>
      <S.PrimaryBtn
        type="button"
        data-editor-text-role-confirm-ok
        disabled={saving}
        onClick={() => {
          const apply = confirm.apply
          setConfirm(null)
          persist(apply)
        }}
      >
        {saving
          ? t('editor.textBox.modals.common.saving')
          : t('editor.textBox.modals.role.updateTheme')}
      </S.PrimaryBtn>
    </S.Footer>
  ) : (
    <S.Footer>
      <S.FooterSpacer />
      <S.GhostBtn type="button" data-editor-text-role-cancel onClick={close}>
        {t('editor.textBox.modals.common.cancel')}
      </S.GhostBtn>
      <S.GhostBtn
        type="button"
        data-editor-text-role-save
        disabled={saving}
        onClick={() => requestSave(false)}
      >
        {saving
          ? t('editor.textBox.modals.common.saving')
          : t('editor.textBox.modals.role.save')}
      </S.GhostBtn>
      <S.PrimaryBtn
        type="button"
        data-editor-text-role-save-apply
        disabled={saving}
        onClick={() => requestSave(true)}
      >
        {saving
          ? t('editor.textBox.modals.common.saving')
          : t('editor.textBox.modals.role.saveAndApply')}
      </S.PrimaryBtn>
    </S.Footer>
  )

  return (
    <AuthoringModalFrame
      backdropTestId="data-editor-text-role-modal-backdrop"
      onDismiss={close}
      onEscape={() => (confirm ? setConfirm(null) : close())}
      dialogAttrs={{ 'data-editor-text-role-modal': true }}
      ariaLabel={t('editor.textBox.modals.role.aria', { role: roleLabel })}
      title={t('editor.textBox.modals.role.title')}
      subtitle={
        <>
          {t('editor.textBox.modals.role.subtitleBefore')}
          <b>{roleLabel}</b>
          {t('editor.textBox.modals.role.subtitleAfter', {
            role: roleLabel,
            roleLower: roleLabel.toLowerCase(),
          })}
        </>
      }
      closeAttrs={{ 'data-editor-text-role-close': true }}
      footer={footer}
    >
      <S.Section>
        <S.SectionLabel>
          {t('editor.textBox.modals.common.role')}
        </S.SectionLabel>
        <div
          data-editor-text-role-picker
          style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
        >
          {ROLES.map((key) => {
            const authoredRole = !!decorations?.[key]
            const active = key === role
            return (
              <button
                key={key}
                type="button"
                data-editor-text-role-pick={key}
                aria-pressed={active}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectRole(key)}
                style={{
                  padding: '6px 10px',
                  borderRadius: 6,
                  border: active ? '1px solid #1d1c1a' : '1px solid #d8d4cf',
                  background: active ? '#1d1c1a' : '#fff',
                  color: active ? '#fff' : '#1d1c1a',
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                {editorTextBoxRoleLabel(t, key)}
                {authoredRole ? ' •' : ''}
              </button>
            )
          })}
        </div>
        <S.Hint>{t('editor.textBox.modals.role.hint')}</S.Hint>
      </S.Section>

      <S.Section>
        <S.SectionLabel>
          {t('editor.textBox.modals.common.preview')}
        </S.SectionLabel>
        <AuthoringPreviewCard
          effective={effective}
          text={roleLabel}
          cardAttrs={{ 'data-editor-text-role-preview': true }}
          textAttrs={{ 'data-editor-text-role-preview-text': true }}
        />
      </S.Section>

      <AuthoringStyleSection
        effective={effective}
        onCommit={commitStyle}
        fontFamilies={FONT_FAMILIES}
        idp="editor-text-role"
        barAttrs={{ 'data-editor-text-role-toolbar': true }}
      >
        {error && (
          <S.ErrorText data-editor-text-role-error>{error}</S.ErrorText>
        )}
      </AuthoringStyleSection>
    </AuthoringModalFrame>
  )
}

export default ThemeRoleDecorationModal
